/**
 * Theme registry — add new dark palettes here, then add matching CSS tokens
 * in oasis-tokens.scss (and optional Sass map in _var.scss).
 */
export const THEME_REGISTRY = [
  {
    id: "light",
    label: "Light",
    mode: "light",
    swatch: { bg: "#eef1f6", surface: "#ffffff", accent: "#2563eb" },
  },
  {
    id: "dark",
    label: "Dark",
    mode: "dark",
    swatch: { bg: "#070a11", surface: "#0f1521", accent: "#3b82f6" },
  },
  {
    id: "black",
    label: "Black",
    mode: "dark",
    swatch: { bg: "#000000", surface: "#0a0a0a", accent: "#3b82f6" },
  },
];

const THEME_SWITCH_CLASS = "theme-switching";
const PREFERRED_DARK_KEY = "ats_preferred_dark_theme";
let themeSwitchClearTimer = null;

const themeById = THEME_REGISTRY.reduce((map, theme) => {
  map[theme.id] = theme;
  return map;
}, {});

export const APP_THEMES = THEME_REGISTRY.map((theme) => theme.id);

export const DARK_THEMES = THEME_REGISTRY.filter((theme) => theme.mode === "dark");

export function getThemeConfig(themeId) {
  return themeById[themeId] || themeById.dark;
}

export function normalizeTheme(theme) {
  return themeById[theme] ? theme : "dark";
}

export function isDarkUiTheme(theme) {
  return getThemeConfig(theme).mode === "dark";
}

export function getPreferredDarkTheme() {
  try {
    const stored = window.localStorage?.getItem(PREFERRED_DARK_KEY);
    if (stored && themeById[stored]?.mode === "dark") return stored;
  } catch {
    /* ignore */
  }
  return "dark";
}

export function setPreferredDarkTheme(themeId) {
  const next = normalizeTheme(themeId);
  if (themeById[next]?.mode !== "dark") return;
  try {
    window.localStorage?.setItem(PREFERRED_DARK_KEY, next);
  } catch {
    /* ignore */
  }
}

/** Body / data-theme class names for a theme id (supports future dark palettes). */
export function getThemeClassNames(themeId) {
  const theme = getThemeConfig(themeId);
  if (theme.mode === "light") {
    return { dataTheme: "light", bodyClasses: ["light-theme"] };
  }
  if (theme.id === "dark") {
    return { dataTheme: "dark", bodyClasses: ["dark-theme"] };
  }
  // Custom dark palette: keep dark-theme for legacy selectors + palette class.
  return {
    dataTheme: theme.id,
    bodyClasses: ["dark-theme", `${theme.id}-theme`],
  };
}

/** Apply theme classes before paint to avoid theme flash. */
export function applyDocumentTheme(theme) {
  const next = normalizeTheme(theme);
  const root = document.documentElement;
  const { dataTheme, bodyClasses } = getThemeClassNames(next);

  root.classList.add(THEME_SWITCH_CLASS);
  root.setAttribute("data-theme", dataTheme);

  const knownBodyThemes = THEME_REGISTRY.flatMap((item) => {
    if (item.mode === "light") return ["light-theme"];
    if (item.id === "dark") return ["dark-theme"];
    return ["dark-theme", `${item.id}-theme`];
  });
  document.body.classList.remove(...Array.from(new Set(knownBodyThemes)));
  document.body.classList.add(...bodyClasses);

  if (themeById[next]?.mode === "dark") {
    setPreferredDarkTheme(next);
  }

  if (themeSwitchClearTimer) {
    window.clearTimeout(themeSwitchClearTimer);
  }

  themeSwitchClearTimer = window.setTimeout(() => {
    root.classList.remove(THEME_SWITCH_CLASS);
    themeSwitchClearTimer = null;
  }, 220);
}
